/**
 * The single authority for which Amplitude event names exist across ArtHelper.
 *
 * Keyed by the event name Amplitude actually receives, so an event fired by more
 * than one app appears exactly once and carries the full list of apps that fire
 * it. Seeded from the three registries that predate this file, renaming nothing:
 *
 *  - client       `Events` in client/src/context/analytics/amplitude/events.ts
 *                 (176 declarations; note two TS keys whose `name` value differs
 *                 from the key — `train_your_assistant_clicked` fires
 *                 `train_your_assistant` and `quick_share_button_clicked` fires
 *                 `share_button_clicked`. This catalog is keyed by the `name`.)
 *  - town_square  `EventNames` in town-square/src/lib/amplitude.ts plus
 *                 `LurkerEngagementEventNames` (150 declarations, plus the
 *                 `image_uploaded` event town-square now shares with the client
 *                 — 151 today)
 *  - api          the names passed to `amplitudeService.trackEvent` (19 seeded
 *                 declarations, plus the 6 `feature_wizard_*` events the feature
 *                 wizards added — 25 today)
 *
 * 176 + 151 + 25 = 352 declarations collapse to 328 distinct events
 * across 24 cross-app overlaps.
 *
 * `group` is claimed by the first registry to declare a name, in the order
 * client -> town_square -> api, so the client's first-class `group` literal wins
 * an overlap. town-square has no `group` field of its own; its groups are the
 * `//` section headers of the interfaces this catalog replaced.
 *
 * This file lists names only. Per-event property schemas stay in the client's
 * `Events` type — town-square and api events have never had typed properties and
 * none are invented here.
 */
export const AMPLITUDE_EVENTS = {
  // ---- api (18) ----
  sn_referral_signup_completed: { apps: ['api'], group: 'api' },
  points_redeemed: { apps: ['api'], group: 'api' },
  /** A newly queued milestone celebration; the denominator for /points views by source. */
  milestone_celebrated: { apps: ['api'], group: 'api' },
  points_redemption_failed: { apps: ['api'], group: 'api' },
  /** A comped-subscription trial (referral gift or redemption) is about to end and we
   * emailed the recipient to convert. Fired as the nudge is enqueued; the conversion funnel
   * is this event → the real paid `customer.subscription.updated`. */
  gift_trial_ending_nudged: { apps: ['api'], group: 'api' },
  profile_claimed: { apps: ['api'], group: 'api' },
  profile_claim_failed: { apps: ['api'], group: 'api' },
  artist_fan_gained: { apps: ['api'], group: 'api' },
  /**
   * The three artwork-comment control signals, and the only three the ticket's
   * secondary metrics need. Deliberately NOT events, so nobody adds them later:
   *
   *  - the standing share of artists with artwork comments ON is a COUNT over
   *    `profiles.isArtworkCommentsEnabled`. It cannot be an event:
   *    the column is NOT NULL DEFAULT true and was never backfilled, and there
   *    is no account-creation event to seed a starting value from — so
   *    `artwork_comments_switch_toggled` measures *changes* and can never
   *    reconstruct the standing share.
   *  - the share of published artworks with at least one comment is a query over
   *    `image_comments`, not a per-artwork event.
   *  - repeat uploads by artists who received a comment joins the existing
   *    `image_uploaded` event against `artist_artwork_comment_received` on the
   *    artist's asfId — both already exist, so no third event is warranted.
   */
  artist_artwork_comment_received: { apps: ['api'], group: 'api' },
  artist_artwork_comment_removed: { apps: ['api'], group: 'api' },
  artist_artwork_commenter_banned: { apps: ['api'], group: 'api' },
  memory_created: { apps: ['api'], group: 'api' },
  instagram_reel_post_success: { apps: ['api'], group: 'api' },
  instagram_carousel_post_success: { apps: ['api'], group: 'api' },
  instagram_single_image_post_success: { apps: ['api'], group: 'api' },
  copyright_scan_started: { apps: ['api'], group: 'api' },
  copyright_scan_completed: { apps: ['api'], group: 'api' },
  copyright_scan_failed: { apps: ['api'], group: 'api' },
  account_deleted: { apps: ['api'], group: 'api' },
  /** An instant vote email sent to a member in their first 5 days (#4096). */
  new_member_vote_email_sent: { apps: ['api'], group: 'api' },
  /**
   * Every accepted connection request, fired server-side so email, banner and app accepts
   * all land in one place. Properties: connectionId, source ('app' | 'email' | 'referral'),
   * minutesToAccept, requesterIsNewMember, requesterInternal, recipientInternal.
   */
  connection_request_accepted: { apps: ['api'], group: 'api' },

  // ---- art_directory (3) ----
  sn_art_card_clicked: { apps: ['town_square'], group: 'art_directory' },
  sn_art_card_artist_name_clicked: { apps: ['town_square'], group: 'art_directory' },
  sn_art_card_title_clicked: { apps: ['town_square'], group: 'art_directory' },

  // ---- art_guard (3) ----
  artguard_results_viewed: { apps: ['client'], group: 'art_guard' },
  artguard_match_clicked: { apps: ['client'], group: 'art_guard' },
  artguard_email_clicked: { apps: ['client'], group: 'art_guard' },

  // ---- artist_directory (1) ----
  sn_artist_card_clicked: { apps: ['town_square'], group: 'artist_directory' },

  // ---- artist_genome (24) ----
  sn_genome_page_viewed: { apps: ['town_square'], group: 'artist_genome' },
  sn_profile_genome_teaser_clicked: { apps: ['town_square'], group: 'artist_genome' },
  sn_genome_connect_clicked: { apps: ['town_square'], group: 'artist_genome' },
  sn_genome_intro_send_clicked: { apps: ['town_square'], group: 'artist_genome' },
  sn_genome_intro_send_success: { apps: ['town_square'], group: 'artist_genome' },
  sn_genome_intro_send_error: { apps: ['town_square'], group: 'artist_genome' },
  sn_genome_intro_modal_dismissed: { apps: ['town_square'], group: 'artist_genome' },
  sn_genome_sent_prompt_shown: { apps: ['town_square'], group: 'artist_genome' },
  sn_genome_sent_prompt_dismissed: { apps: ['town_square'], group: 'artist_genome' },
  sn_genome_message_added_clicked: { apps: ['town_square'], group: 'artist_genome' },
  sn_genome_message_added_success: { apps: ['town_square'], group: 'artist_genome' },
  sn_genome_message_added_error: { apps: ['town_square'], group: 'artist_genome' },
  sn_genome_hma_modal_opened: { apps: ['town_square'], group: 'artist_genome' },
  sn_genome_hma_verify_clicked: { apps: ['town_square'], group: 'artist_genome' },
  sn_genome_axis_tab_changed: { apps: ['town_square'], group: 'artist_genome' },
  sn_genome_globe_marker_clicked: { apps: ['town_square'], group: 'artist_genome' },
  sn_genome_use_my_location_clicked: { apps: ['town_square'], group: 'artist_genome' },
  sn_genome_geolocation_granted: { apps: ['town_square'], group: 'artist_genome' },
  sn_genome_geolocation_denied: { apps: ['town_square'], group: 'artist_genome' },
  sn_genome_signup_clicked: { apps: ['town_square'], group: 'artist_genome' },
  sn_genome_signin_clicked: { apps: ['town_square'], group: 'artist_genome' },
  sn_genome_hma_pitch_verify_clicked: { apps: ['town_square'], group: 'artist_genome' },
  sn_genome_globe_opened: { apps: ['town_square'], group: 'artist_genome' },
  sn_genome_profile_clicked: { apps: ['town_square'], group: 'artist_genome' },

  // ---- arty_sparklink (7) ----
  sn_arty_sparklink_landing: { apps: ['town_square', 'client'], group: 'arty_sparklink' },
  sn_arty_panel_opened: { apps: ['town_square', 'client'], group: 'arty_sparklink' },
  sn_arty_panel_closed: { apps: ['town_square'], group: 'arty_sparklink' },
  sn_arty_panel_expanded: { apps: ['town_square'], group: 'arty_sparklink' },
  sn_arty_panel_restored: { apps: ['town_square'], group: 'arty_sparklink' },
  sn_arty_message_sent: { apps: ['town_square'], group: 'arty_sparklink' },
  sn_arty_community_action_clicked: { apps: ['town_square'], group: 'arty_sparklink' },
  sn_arty_daily_sparklink_shown: { apps: ['town_square'], group: 'arty_sparklink' },
  sn_arty_daily_sparklink_clicked: { apps: ['town_square'], group: 'arty_sparklink' },
  /**
   * The combined visibility chip on an Arty artwork card (the assistant image UI
   * and the completed-upload card), carrying `image_id` and the new `is_public`.
   * A change signal — one event per flip — never a level.
   */
  arty_artwork_visibility_toggled: { apps: ['town_square', 'client'], group: 'arty_sparklink' },
  sn_arty_daily_sparklink_dismissed: { apps: ['town_square'], group: 'arty_sparklink' },
  /**
   * The Arty image action card and its actions sheet, mirroring new arty's PostHog
   * set. Each is one event per user action, carrying `image_id` where a piece is in view.
   */
  arty_card_action_run: { apps: ['town_square', 'client'], group: 'arty_sparklink' },
  arty_action_pin_toggled: { apps: ['town_square', 'client'], group: 'arty_sparklink' },
  arty_card_sheet_opened: { apps: ['town_square', 'client'], group: 'arty_sparklink' },
  /**
   * An Arty image card drawn in a transcript, once per piece per page load, carrying `image_id`.
   * The denominator the card's `arty_card_action_run` and `arty_card_sheet_opened` are read
   * against; both of those carry `entry: 'card' | 'menu'` to split chat cards from image menus.
   */
  arty_card_shown: { apps: ['town_square', 'client'], group: 'arty_sparklink' },
  /**
   * Client's inline community ask (critique, rating, pricing or poll) posted from the Arty
   * card, carrying `kind`, `post_id` and `image_id`. New arty's PostHog name for the same
   * moment; one event per post that landed.
   */
  arty_community_ask_posted: { apps: ['client'], group: 'arty_sparklink' },
  // ---- auth (6) ----
  sn_account_created: { apps: ['town_square'], group: 'auth' },
  sn_signed_in: { apps: ['town_square'], group: 'auth' },
  sn_sign_in_page_viewed: { apps: ['town_square'], group: 'auth' },
  sn_sign_up_page_viewed: { apps: ['town_square'], group: 'auth' },
  sn_signup_email_suggestion_shown: { apps: ['town_square'], group: 'auth' },
  sn_signup_email_suggestion_accepted: { apps: ['town_square'], group: 'auth' },

  // ---- claim_flow (7) ----
  /** The claim bar on an unclaimed profile — the top of the funnel. */
  sn_claim_banner_clicked: { apps: ['town_square'], group: 'claim_flow' },
  /** An imported profile was viewed by anyone. The first place SEO shows up. */
  sn_unclaimed_profile_viewed: { apps: ['town_square'], group: 'claim_flow' },
  sn_claim_page_viewed: { apps: ['town_square'], group: 'claim_flow' },
  sn_claim_support_clicked: { apps: ['town_square'], group: 'claim_flow' },
  sn_claim_email_submitted: { apps: ['town_square'], group: 'claim_flow' },
  sn_claim_email_verified: { apps: ['town_square'], group: 'claim_flow' },
  sn_claim_google_oauth_started: { apps: ['town_square'], group: 'claim_flow' },
  sn_claim_account_created: { apps: ['town_square'], group: 'claim_flow' },
  sn_claim_blocked_existing_account: { apps: ['town_square'], group: 'claim_flow' },

  // ---- connect_dialog (15) ----
  sn_profile_connect_clicked: { apps: ['town_square'], group: 'connect_dialog' },
  sn_profile_connect_dialog_dismissed: { apps: ['town_square'], group: 'connect_dialog' },
  sn_profile_connect_send_clicked: { apps: ['town_square'], group: 'connect_dialog' },
  sn_profile_connect_send_success: { apps: ['town_square'], group: 'connect_dialog' },
  sn_profile_connect_send_error: { apps: ['town_square'], group: 'connect_dialog' },
  sn_home_rec_connect_clicked: { apps: ['town_square'], group: 'connect_dialog' },
  sn_home_rec_connect_dialog_dismissed: { apps: ['town_square'], group: 'connect_dialog' },
  sn_home_rec_connect_send_clicked: { apps: ['town_square'], group: 'connect_dialog' },
  sn_home_rec_connect_send_success: { apps: ['town_square'], group: 'connect_dialog' },
  sn_home_rec_connect_send_error: { apps: ['town_square'], group: 'connect_dialog' },
  sn_profile_connect_sent_prompt_shown: { apps: ['town_square'], group: 'connect_dialog' },
  sn_profile_connect_sent_prompt_dismissed: { apps: ['town_square'], group: 'connect_dialog' },
  sn_profile_connect_message_added_clicked: { apps: ['town_square'], group: 'connect_dialog' },
  sn_profile_connect_message_added_success: { apps: ['town_square'], group: 'connect_dialog' },
  sn_profile_connect_message_added_error: { apps: ['town_square'], group: 'connect_dialog' },

  // ---- contact_form (3) ----
  sn_contact_artist_button_clicked: { apps: ['town_square'], group: 'contact_form' },
  sn_contact_artist_form_submit_clicked: { apps: ['town_square'], group: 'contact_form' },
  sn_contact_artist_form_submit_success: { apps: ['town_square'], group: 'contact_form' },

  // ---- content_review (2) ----
  sn_content_review_requested: { apps: ['town_square'], group: 'content_review' },
  sn_report_button_clicked: { apps: ['town_square'], group: 'content_review' },

  // ---- conversion_funnel (3) ----
  hit_paywall: { apps: ['client'], group: 'conversion_funnel' },
  clicked_upgrade: { apps: ['client'], group: 'conversion_funnel' },
  converted_to_paid: { apps: ['client'], group: 'conversion_funnel' },

  // ---- custom_art (9) ----
  image_uploaded: { apps: ['client', 'town_square'], group: 'custom_art' },
  /** An upload that never became artwork, with the `step` it broke at (#4159). See shared/upload-failure.ts. */
  image_upload_failed: { apps: ['client', 'town_square'], group: 'custom_art' },
  image_clicked: { apps: ['client'], group: 'custom_art' },
  change_request: { apps: ['client'], group: 'custom_art' },
  sparks_clicked: { apps: ['client'], group: 'custom_art' },
  details_saved: { apps: ['client'], group: 'custom_art' },
  custom_rules_saved: { apps: ['client'], group: 'custom_art' },
  custom_voice_saved: { apps: ['client'], group: 'custom_art' },
  artwork_details_updated: { apps: ['client'], group: 'custom_art' },

  // ---- customer_registration (2) ----
  /** @description Triggers when user taps the sign up button on the marketing site */
  signup_webflow_clicked: { apps: ['client'], group: 'customer_registration' },
  account_created: { apps: ['client', 'api'], group: 'customer_registration' },

  // ---- feature_wizard (6) ----
  /** @description User landed on a page carrying a feature wizard's slug (both audiences) */
  feature_wizard_arrived: { apps: ['api'], group: 'feature_wizard' },
  /** @description Wizard-audience user opened the wizard overlay */
  feature_wizard_opened: { apps: ['api'], group: 'feature_wizard' },
  /** @description User was shown one step of a wizard */
  feature_wizard_step_viewed: { apps: ['api'], group: 'feature_wizard' },
  /** @description User acted on one step of a wizard */
  feature_wizard_step_submitted: { apps: ['api'], group: 'feature_wizard' },
  /** @description User finished a wizard */
  feature_wizard_completed: { apps: ['api'], group: 'feature_wizard' },
  /** @description User closed a wizard without finishing it */
  feature_wizard_dismissed: { apps: ['api'], group: 'feature_wizard' },

  // ---- interaction_events (75) ----
  mockup_download: { apps: ['client'], group: 'interaction_events' },
  referral_link_copied: { apps: ['client'], group: 'interaction_events' },
  asf_website_clicked: { apps: ['client'], group: 'interaction_events' },
  new_folder_created: { apps: ['client'], group: 'interaction_events' },
  integrations_nav_link_clicked: { apps: ['client'], group: 'interaction_events' },
  billing_nav_link_clicked: { apps: ['client'], group: 'interaction_events' },
  profile_nav_link_clicked: { apps: ['client'], group: 'interaction_events' },
  contacts_nav_link_clicked: { apps: ['client'], group: 'interaction_events' },
  notifications_nav_link_clicked: { apps: ['client'], group: 'interaction_events' },
  /**
   * @description The Feedback row pinned at the foot of the client and town-square
   * sidebars — out to the Canny board. Its own name rather than a generic nav click
   * because it answers a different question from the rows above it: not "where do people
   * go" but "how often does somebody get stuck". It is also the ONLY attribution
   * available — Canny's create form ignores every query parameter, so a post can never be
   * traced back to an app. Both hosts fire the one name; `location` is the only thing
   * separating them.
   */
  feedback_nav_link_clicked: { apps: ['client', 'town_square'], group: 'interaction_events' },
  /**
   * @description The Tutorials row directly above Feedback, into the in-app walkthrough
   * library. RENAMED from `how_to_videos_nav_link_clicked`, which was declared here for a
   * long time and never once fired — the avatar-menu item it belonged to called
   * `window.open` with no track — so no history is orphaned by the rename, and the
   * question "does anyone use the walkthroughs?" becomes answerable for the first time.
   *
   * Both hosts fire the one name, as they do for Feedback beside it. The walkthrough
   * library is a client page either way, so `location` — `sidebar`/`sidebar_mobile` from
   * the client, `town_square_sidebar`/`town_square_sidebar_mobile` from town-square — is
   * the only thing saying which rail the artist reached for.
   */
  tutorials_nav_link_clicked: { apps: ['client', 'town_square'], group: 'interaction_events' },
  /**
   * @description A walkthrough actually played. The row above says somebody went looking;
   * this says which clip they chose, which is what decides what gets recorded next.
   * town-square fires it from the guild pages' own Watch tutorial trigger.
   */
  tutorial_video_opened: { apps: ['client', 'town_square'], group: 'interaction_events' },
  TOS_nav_link_clicked: { apps: ['client'], group: 'interaction_events' },
  calendar_nav_link_clicked: { apps: ['client'], group: 'interaction_events' },
  saved_content_nav_link_clicked: { apps: ['client'], group: 'interaction_events' },
  chat_nav_link_clicked: { apps: ['client'], group: 'interaction_events' },
  my_artwork_nav_link_clicked: { apps: ['client'], group: 'interaction_events' },
  admin_nav_link_clicked: { apps: ['client'], group: 'interaction_events' },
  network_nav_link_clicked: { apps: ['client'], group: 'interaction_events' },
  messages_nav_link_clicked: { apps: ['client'], group: 'interaction_events' },
  /**
   * @description Fires when a viewer activates an app in the suite nav bar — desktop tile
   * or mobile dropdown row, one emit per activation. Both hosts that mount the bar emit it
   * with identical property names so weekly click-throughs group on `target_app_id`.
   */
  suite_nav_app_clicked: { apps: ['client', 'town_square'], group: 'interaction_events' },
  connect_ig_clicked: { apps: ['client'], group: 'interaction_events' },
  connected_ig_success: { apps: ['client'], group: 'interaction_events' },
  collection_spark_btn_clicked: { apps: ['client'], group: 'interaction_events' },
  instagram_post_success: { apps: ['client'], group: 'interaction_events' },
  instagram_post_scheduled: { apps: ['client'], group: 'interaction_events' },
  instagram_post_error: { apps: ['client'], group: 'interaction_events' },
  saved_content_clicked: { apps: ['client'], group: 'interaction_events' },
  spark_content_saved: { apps: ['client'], group: 'interaction_events' },
  spark_content_edited: { apps: ['client'], group: 'interaction_events' },
  spark_content_regenerated: { apps: ['client'], group: 'interaction_events' },
  mockup_saved: { apps: ['client'], group: 'interaction_events' },
  mockup_popup_button_clicked: { apps: ['client'], group: 'interaction_events' },
  etsy_any_sparks_ran: { apps: ['client'], group: 'interaction_events' },
  clicked_grow_your_audience_link: { apps: ['client'], group: 'interaction_events' },
  clicked_art_marketing_autopilot_link: { apps: ['client'], group: 'interaction_events' },
  home_nav_link_clicked: { apps: ['client'], group: 'interaction_events' },
  community_forum_nav_link_clicked: { apps: ['client'], group: 'interaction_events' },
  chat_message_sent: { apps: ['client'], group: 'interaction_events' },
  chat_message_copied: { apps: ['client'], group: 'interaction_events' },
  chat_message_shared: { apps: ['client'], group: 'interaction_events' },
  chat_prompt_shared: { apps: ['client'], group: 'interaction_events' },
  chat_rating_clicked: { apps: ['client'], group: 'interaction_events' },
  /** User opened a post from the community-discussion carousel rendered after search_community_discussions */
  chat_community_discussion_card_clicked: { apps: ['client'], group: 'interaction_events' },
  /** User opened a post from the standalone chat trending sidebar */
  chat_trending_community_post_clicked: { apps: ['client'], group: 'interaction_events' },
  train_your_assistant: { apps: ['client'], group: 'interaction_events' },
  assistant_settings_updated: { apps: ['client'], group: 'interaction_events' },
  notification_settings_updated: { apps: ['client'], group: 'interaction_events' },
  email_unsubscribed: { apps: ['client'], group: 'interaction_events' },
  chat_imagebox_edit_details_clicked: { apps: ['client'], group: 'interaction_events' },
  chat_imagebox_room_mockups_clicked: { apps: ['client'], group: 'interaction_events' },
  chat_imagebox_new_post_clicked: { apps: ['client'], group: 'interaction_events' },
  chat_imagebox_artguard_clicked: { apps: ['client'], group: 'interaction_events' },
  chat_imagebox_share_clicked: { apps: ['client'], group: 'interaction_events' },
  my_artwork_image_explore_with_ai_clicked: { apps: ['client'], group: 'interaction_events' },
  my_artwork_image_visibility_toggled: { apps: ['client'], group: 'interaction_events' },
  my_artwork_bulk_visibility_toggled: { apps: ['client'], group: 'interaction_events' },
  my_artwork_image_room_mockups_clicked: { apps: ['client'], group: 'interaction_events' },
  my_artwork_image_view_clicked: { apps: ['client'], group: 'interaction_events' },
  /** My Artwork's per-image "..." menu opened; the denominator for its item clicks. */
  my_artwork_image_menu_opened: { apps: ['client'], group: 'interaction_events' },
  my_artwork_image_edit_clicked: { apps: ['client'], group: 'interaction_events' },
  notification_take_action_share_link_clicked: { apps: ['client'], group: 'interaction_events' },
  notification_take_action_post_to_social_clicked: { apps: ['client'], group: 'interaction_events' },
  share_button_clicked: { apps: ['client'], group: 'interaction_events' },
  chat_imagebox_view_clicked: { apps: ['client'], group: 'interaction_events' },
  download_all_mockups_clicked: { apps: ['client'], group: 'interaction_events' },
  download_all_mockups_completed: { apps: ['client'], group: 'interaction_events' },
  content_generation_triggered: { apps: ['client'], group: 'interaction_events' },
  artguard_tab_clicked: { apps: ['client'], group: 'interaction_events' },
  artguard_modal_opened: { apps: ['client'], group: 'interaction_events' },
  artguard_view_full_page_clicked: { apps: ['client'], group: 'interaction_events' },
  artguard_notification_clicked: { apps: ['client'], group: 'interaction_events' },
  activity_notification_email_clicked: { apps: ['client'], group: 'interaction_events' },
  notification_take_action_post_clicked: { apps: ['client'], group: 'interaction_events' },
  notification_take_action_download_mockups_clicked: { apps: ['client'], group: 'interaction_events' },
  notification_take_action_edit_details_clicked: { apps: ['client'], group: 'interaction_events' },
  notification_take_action_explore_with_ai_clicked: { apps: ['client'], group: 'interaction_events' },
  notification_take_action_view_clicked: { apps: ['client'], group: 'interaction_events' },
  notification_view_results_clicked: { apps: ['client'], group: 'interaction_events' },
  /**
   * THE NOTIFICATION MODALS: does answering in place actually happen?
   *
   * The whole bet is that a reader who stays on the page sends more than one reaction.
   * `*_opened` is the denominator for that — every tap below is only interesting as a
   * rate against the modal that offered it — and `notification_post_open_in_community_clicked`
   * is the honest counter-metric: the modal was not enough and they left anyway.
   */
  notification_new_artwork_opened: { apps: ['client'], group: 'interaction_events' },
  notification_artwork_expanded: { apps: ['client', 'town_square'], group: 'interaction_events' },
  /**
   * The one-tap like on a new member's follow row (#4479): the active member hearted the
   * newcomer's latest piece straight from "X followed you". The newcomer gets the ordinary
   * `like` notification; this is the rate against follow rows that showed the piece.
   */
  follow_notification_art_liked: { apps: ['client'], group: 'interaction_events' },
  notification_artist_work_opened: { apps: ['client'], group: 'interaction_events' },
  notification_add_more_opened: { apps: ['client'], group: 'interaction_events' },
  notification_artwork_comment_sent: { apps: ['client', 'town_square'], group: 'interaction_events' },
  notification_post_opened: { apps: ['client'], group: 'interaction_events' },
  notification_post_voted: { apps: ['client'], group: 'interaction_events' },
  notification_post_comment_sent: { apps: ['client'], group: 'interaction_events' },
  notification_dm_quick_reply_sent: { apps: ['client'], group: 'interaction_events' },
  notification_post_open_in_community_clicked: { apps: ['client'], group: 'interaction_events' },
  /**
   * THE ONE-TAP REPLIES AND THE REACTION BAR.
   *
   * These are the mechanics the whole plan rests on, so they are the ones whose tap rate
   * actually answers whether it worked. `notification_acknowledgement_refused` is the
   * honest half: a button that is offered and then refuses is worse than one that is not
   * offered, and this is how that shows up as a number rather than a complaint.
   */
  notification_acknowledgement_sent: { apps: ['client'], group: 'interaction_events' },
  notification_acknowledgement_refused: { apps: ['client'], group: 'interaction_events' },
  notification_people_list_opened: { apps: ['client'], group: 'interaction_events' },
  notification_wip_share_opened: { apps: ['client'], group: 'interaction_events' },
  notification_wip_shared: { apps: ['client'], group: 'interaction_events' },
  notification_wip_dm_opened: { apps: ['client'], group: 'interaction_events' },
  notification_milestone_link_clicked: { apps: ['client'], group: 'interaction_events' },
  artwork_reaction_toggled: { apps: ['client', 'town_square'], group: 'interaction_events' },
  community_post_welcomed: { apps: ['town_square'], group: 'interaction_events' },
  notification_view_clicked: { apps: ['client'], group: 'interaction_events' },
  /** @description Fires when a user submits the Request Review dialog for a flagged image. */
  content_review_requested: { apps: ['client'], group: 'interaction_events' },

  // ---- like_button (1) ----
  sn_like_button_clicked: { apps: ['town_square'], group: 'like_button' },

  // ---- lurker_engagement (19) ----
  // Lurker-engagement event taxonomy — issue #2452.
  // These intentionally lack the `sn_` prefix (D1 decision in PRD 1779988072
  // "Things the Next Engineer Should Know → Feature 1 decisions"): they match
  // the issue spec verbatim so the CPO Town Square Watch dashboard SQL keys
  // against the exact strings without a naming-form translation step. Do NOT
  // "fix" the inconsistency by adding `sn_`. Four are exit-shaped (dwell /
  // session_active_ms) and emit via `trackOnExit` (beacon transport) on page
  // exit.
  /**
   * Fires on /community page exit with the user's accumulated active-vs-idle
   * time over the visit — the canonical "engaged time" lurker metric for the
   * community feed surface. Distinct from wall-clock time: tabs in background
   * (`document.hidden`) accumulate as paused, and 30s of no scroll/click/
   * keydown buckets time as `idleMs` instead of `activeMs`.
   *
   * Two exit paths drain through this single emission (all via beacon):
   *  (a) tab/window close → `pagehide`
   *  (b) SPA navigation away from /community → component unmount cleanup
   *
   * Wired by `CommunityFeedSessionTracker` (mounted in /community/page.tsx)
   * which calls `useActiveTime` for the timer + `useExitEvent` for the
   * three exit paths. Beacon transport (`navigator.sendBeacon`) means the
   * event survives page-unload races that XHR would silently drop.
   *
   * Properties:
   *  - active_ms: number — milliseconds the user was actively interacting
   *    (page visible AND <30s since last scroll/click/keydown)
   *  - idle_ms: number — milliseconds the page was visible but idle
   *  - total_session_ms: number — total time the tracker was running
   *    (= active_ms + idle_ms; excludes time when document was hidden)
   */
  community_feed_session_active_ms: { apps: ['town_square'], group: 'lurker_engagement' },
  /**
   * Fires on /community/<slug>/post/<postSlug> page exit with the user's
   * accumulated active-vs-idle time over the visit — the detail-page
   * counterpart to `community_feed_post_dwell` and the canonical "engaged
   * reading time" metric for an individual community post. Distinct from
   * wall-clock time: tabs in background (`document.hidden`) accumulate as
   * paused, and 30s of no scroll/click/keydown buckets time as `idleMs`
   * instead of `activeMs`.
   *
   * Two exit paths drain through this single emission (all via beacon):
   *  (a) tab/window close → `pagehide`
   *  (b) SPA navigation away from the post detail route → unmount cleanup
   *      (Next.js App Router unmounts the route segment on nav to any other
   *      route, so the unmount-fire path in `useExitEvent` catches it).
   *
   * Wired by `CommunityPostDwellTracker` (mounted in the post detail page
   * server component) which calls `useActiveTime` for the timer +
   * `useExitEvent` for the three exit paths. The tracker captures `postId`
   * at mount and emits it alongside the active-time snapshot so the
   * dashboard can join dwell against `community_feed_post_card_clicked` on
   * the same post_id to measure "time-spent-after-click".
   *
   * Pair this with `community_feed_post_dwell` (F6) for the full reading
   * funnel: feed-card dwell (passive scroll past) → card click → post-detail
   * dwell (active reading). Both events share the bare `post_id` property
   * for join compatibility.
   *
   * Properties:
   *  - post_id: string — community post id
   *  - active_ms: number — milliseconds the user was actively interacting
   *    (page visible AND <30s since last scroll/click/keydown)
   *  - idle_ms: number — milliseconds the page was visible but idle
   *  - total_session_ms: number — total time the tracker was running
   *    (= active_ms + idle_ms; excludes time when document was hidden)
   */
  community_post_dwell_active_ms: { apps: ['town_square'], group: 'lurker_engagement' },
  /**
   * Fires on artist-profile-page exit (`/<slug>` and its child tab routes
   * `/about`, `/artist-statement`, `/activity`, `/awards`, `/room-mockups`)
   * with the viewer's accumulated active-vs-idle time over the visit — the
   * LinkedIn-shaped "real time spent investigating other artists' profiles"
   * signal for the H2b acquisition surface. Distinct from wall-clock time:
   * tabs in background (`document.hidden`) accumulate as paused, and 30s of
   * no scroll/click/keydown buckets time as `idleMs` instead of `activeMs`.
   *
   * Two exit paths drain through this single emission (all via beacon):
   *  (a) tab/window close → `pagehide`
   *  (b) SPA navigation away from the profile route → unmount cleanup
   *      (Next.js App Router unmounts the route segment on nav to any other
   *      route, so the unmount-fire path in `useExitEvent` catches it).
   *
   * Granularity caveat — profile tabs are SEPARATE Next.js route segments
   * (`/<slug>/page.tsx`, `/<slug>/about/page.tsx`, `/<slug>/activity/page.tsx`,
   * etc.), so navigating between tabs unmounts `ProfileLayout` and remounts a
   * fresh tracker. Each tab visit therefore emits its own
   * `profile_view_dwell_active_ms` event. The dashboard SQL writer can sum
   * across same-session-same-`profile_user_id` events to reconstruct the full
   * "time on this profile" figure, OR analyze per-tab (e.g. did this lurker
   * read the artist statement?). Per-tab granularity is a SUPERSET of the
   * roll-up signal — the rollup is a `SUM(active_ms) GROUP BY session_id,
   * profile_user_id` away.
   *
   * Wired by `ProfileViewDwellTracker` (mounted in `ProfileLayout`, the
   * primary client wrapper rendered by every tab's page.tsx) which calls
   * `useActiveTime` for the timer + `useExitEvent` for the three exit paths.
   * Beacon transport (`navigator.sendBeacon`) means the event survives
   * page-unload races that XHR would silently drop.
   *
   * Properties:
   *  - profile_user_id: string — the artist's `userId` (asfId, the BIGINT-as-
   *    string identifier; NOT the artist's slug)
   *  - viewer_role: 'anon' | 'signed_in' | 'anchor' — relationship of the
   *    viewer to the profile owner. Derived in the tracker from the SSR-
   *    resolved Clerk auth state:
   *      'anon'      = no Clerk session (`currentUserId` falsy)
   *      'anchor'    = Clerk session AND `viewerAsfId === profileUserId`
   *                    (viewer is the profile owner)
   *      'signed_in' = Clerk session AND not the owner (or `viewerAsfId`
   *                    missing — degenerate Clerk metadata case treated as
   *                    "signed in but not the anchor")
   *  - active_ms: number — milliseconds the user was actively interacting
   *    (page visible AND <30s since last scroll/click/keydown)
   *  - idle_ms: number — milliseconds the page was visible but idle
   *  - total_session_ms: number — total time the tracker was running
   *    (= active_ms + idle_ms; excludes time when document was hidden).
   *    Included for shape parity with `community_feed_session_active_ms`
   *    (F9) and `community_post_dwell_active_ms` (F11) — the PRD step
   *    description omits it but the step body uses `...activeTime.flush()`
   *    which spreads all three keys, and dashboard SQL is simpler with a
   *    consistent 3-tuple across every `*_dwell_active_ms` /
   *    `*_session_active_ms` event.
   */
  profile_view_dwell_active_ms: { apps: ['town_square'], group: 'lurker_engagement' },
  /**
   * Fires on town-square app exit with the user's accumulated active-vs-idle
   * time across the WHOLE session — the most blunt instrument for "is this
   * user actually using the product?" — answers "how much real engaged time
   * did the user spend in town-square this session?" across every route they
   * visited. Distinct from wall-clock time: tabs in background
   * (`document.hidden`) accumulate as paused, and 30s of no scroll/click/
   * keydown buckets time as `idleMs` instead of `activeMs`.
   *
   * Two exit paths drain through this single emission (all via beacon):
   *  (a) tab/window close → `pagehide`
   *  (b) locale layout unmount (rare — only on cross-locale navigation, since
   *      route-level navs within `/[locale]/...` keep the layout mounted)
   *
   * SCOPE NOTE — app-level, NOT per-route. The tracker mounts in the locale
   * layout (alongside `<AmplitudeUserIdentifier>`) and stays mounted across
   * SPA navigation between /community, /verify, /<slug>, etc. so the active-
   * time accumulator captures the FULL session. This is distinct from F9's
   * `community_feed_session_active_ms` (scoped to /community visits) and
   * F11's `community_post_dwell_active_ms` (scoped to single post-detail
   * visits) — those measure per-surface dwell; `session_active_ms` measures
   * the whole-app rollup.
   *
   * AMPLITUDE SESSION-END HOOK — Amplitude SDK v2.11.x has NO `onSessionEnd`
   * callback. It exposes `getSessionId(): number | undefined`, `setSessionId`,
   * `extendSession`, and a `sessionTimeout` config (defaults to 30min) — but
   * no event-fire when the session rotates. The pragmatic alternative is the
   * `useExitEvent` page-exit path: almost every browser session ends with
   * pagehide / unmount. The 30min-idle-in-foreground-tab
   * edge case is the only one not covered — and the eventual exit will fire
   * the event with the (longer) accumulated dwell, which is MORE faithful to
   * actual engagement than emitting at the artificial 30min boundary. If
   * future telemetry shows the 30min edge case matters, a `setInterval`
   * polling `getSessionId()` for changes would close the gap; not shipped
   * here because the cost (perpetual timer) is not yet justified.
   *
   * CROSS-APP DUAL-EMIT — explicitly NOT added to
   * `client/src/context/analytics/amplitude/amplitude.ts` for first ship
   * (PRD D3 decision): the lurker cohort the dashboard measures lives in
   * town-square by definition; client/ users are post-activation and tell a
   * different story. If the dashboard surfaces a real cross-app-session need,
   * add the matching tracker to `client/`'s amplitude init at that point.
   *
   * Wired by `SessionTracker` (mounted in town-square/src/app/[locale]/
   * layout.tsx) which calls `useActiveTime` for the timer + `useExitEvent`
   * for the three exit paths. Beacon transport (`navigator.sendBeacon`)
   * means the event survives page-unload races that XHR would silently drop.
   *
   * Properties (per issue #2452 spec — note `total_ms`, not `total_session_ms`
   * like F9/F11/F14, because the spec specifies bare `total_ms` for this
   * event and the dashboard SQL keys against the documented name):
   *  - active_ms: number — milliseconds the user was actively interacting
   *    (page visible AND <30s since last scroll/click/keydown)
   *  - idle_ms: number — milliseconds the page was visible but idle
   *  - total_ms: number — total time the tracker was running
   *    (= active_ms + idle_ms; excludes time when document was hidden)
   */
  session_active_ms: { apps: ['town_square'], group: 'lurker_engagement' },
  /**
   * Fires on town-square app exit with the count of DISTINCT /community/*
   * pathnames the user visited this session, plus the count of unique
   * community slugs touched — the light-tier (lurker) activation signal that
   * answers "how many community pages did this user actually touch this
   * session?". One of the low-friction activation thresholds the CPO Town
   * Square Watch dashboard tracks alongside `session_active_ms`.
   *
   * Two exit paths drain through this single emission (all via beacon),
   * piggybacking on `session_active_ms`'s `useExitEvent` listeners:
   *  (a) tab/window close → `pagehide`
   *  (b) locale layout unmount (rare — cross-locale nav only)
   *
   * SCOPE NOTE — app-level, NOT per-route. Wired alongside `session_active_ms`
   * in `SessionTracker` (mounted in the locale layout) via the dedicated
   * `useCommunityPagesPerSession` hook. Pathname tracking uses next-intl's
   * locale-stripped `usePathname` from `@/i18n/navigation`, so the locale
   * prefix never enters the visited-paths Set — `/en/community/foo` and
   * `/fr/community/foo` both contribute the single path `/community/foo`.
   *
   * What counts as a "community page":
   *  - exact match `/community` (the root feed)
   *  - any path starting with `/community/` (e.g. `/community/all`,
   *    `/community/<slug>`, `/community/<slug>/post/<postSlug>`)
   *  - `/communities` (a different surface) does NOT match — the slash
   *    boundary in the prefix check (`/community/`) prevents it
   *  - query strings and hash fragments are stripped by `usePathname`
   *    automatically, so `/community?tab=trending` is counted as `/community`
   *
   * Unique community slug extraction: split the pathname on `/`. The third
   * segment (after the empty leading-slash element and `community`) is the
   * slug. `/community` alone has no slug (no segment[2]) and only increments
   * `pages_count`, not `unique_communities_visited`. `/community/<slug>` and
   * `/community/<slug>/post/<postSlug>` both contribute the same `<slug>` —
   * so visiting one post on a community counts as one "unique community
   * visited", not two.
   *
   * Properties:
   *  - pages_count: number — Set.size of distinct /community/* pathnames
   *    visited this session
   *  - unique_communities_visited: number — Set.size of distinct community
   *    slugs extracted from those pathnames
   *
   * Note: the PRD's manual verification recipe ("visit /community,
   * /community/<slugA>, /community/<slugB>/post/<postSlug>, /community/<slugA>
   * again → expect pages_count=4") has an off-by-one — `/community/<slugA>`
   * appears twice but the Set dedupes to a single entry, so the actual
   * `pages_count` for that recipe is 3 (`/community`, `/community/<slugA>`,
   * `/community/<slugB>/post/<postSlug>`). `unique_communities_visited=2`
   * (slugA + slugB) IS correct per the recipe. Documented for F19 verify pass.
   */
  community_pages_per_session: { apps: ['town_square'], group: 'lurker_engagement' },
  /**
   * Fires when ≥50% of a community post card is in the viewport for ≥500ms
   * in the infinite-scroll feed. The foundational "post-read" signal replacing
   * meaningless scroll_50% on infinite scroll. Wired via FeedPostTracker around
   * each CommunityPostCard. At most one event per card per mount.
   *
   * Properties:
   *  - post_id: string — community post id
   *  - position_in_feed: number — 0-indexed position in the visible feed list
   *  - community_id: string | undefined — the post's owning community id
   *  - feed_tab: string — 'trending' | 'following' | 'new'
   *    on /community, or 'community' on /community/[slug]
   */
  community_feed_post_viewed: { apps: ['town_square'], group: 'lurker_engagement' },
  /**
   * Fires when a previously-viewed community feed post card leaves the
   * viewport — the FB/IG "scroll-stop time" signal and the single best
   * lurker-engagement metric for the infinite feed. Two emission paths:
   *  (a) in-flow onLeave from useViewportTracker (regular XHR transport), and
   *  (b) page-close exit flush (beacon transport via trackOnExit) so dwell
   *      data survives unload. Both paths drain the same per-feed registry,
   *      so a card emits at most ONE dwell event per visibility session.
   *
   * Properties:
   *  - post_id: string
   *  - dwell_ms: number — milliseconds the card was ≥50% visible (matches the
   *    useViewportTracker debouncer's measurement)
   *  - scroll_direction: 'up' | 'down' — window.scrollY at onEnter vs at
   *    leave/exit; ties (scrollY unchanged) bias to 'down'
   *  - position_in_feed: number
   *  - community_id: string | undefined
   *  - feed_tab: string — same semantics as community_feed_post_viewed.feed_tab
   */
  community_feed_post_dwell: { apps: ['town_square'], group: 'lurker_engagement' },
  /**
   * Fires once per successful infinite-scroll batch fetch on the community
   * feed (/community tabs and /community/[slug] per-community feed). Heavy
   * proxy for engagement depth — answers "how deep did this lurker go into
   * the feed?" on a per-session basis. Wired via useFeedLoadMoreTracker
   * inside FeedList (in CommunityFeedTabs) and PostList; called from each
   * surface's onLoadMore handler after the fetchMore/client.query resolves.
   *
   * Counter semantics (issue #2452 spec):
   *  - batch_number is 1-indexed and counts only infinite-scroll batches
   *    (NOT the initial page load).
   *  - total_posts_in_session is the running sum of posts_loaded across
   *    batches in this mount cycle (NOT including the initial load).
   *  - Both counters reset on mount/remount, which means tab switches on
   *    /community (each tab is a distinct <FeedList>) and navigations
   *    away/back to /community/[slug] start a fresh feed session.
   *
   * Properties:
   *  - batch_number: number — 1-indexed
   *  - posts_loaded: number — new posts in this batch (may be 0 at feed end)
   *  - total_posts_in_session: number — running sum across batches
   *  - feed_tab: string — same semantics as community_feed_post_viewed.feed_tab
   *    ('trending' | 'following' | 'new' on /community, or
   *    'community' on /community/[slug])
   */
  community_feed_load_more: { apps: ['town_square'], group: 'lurker_engagement' },
  /**
   * Fires when a user clicks into a community post's detail from the feed —
   * the active-engagement signal that complements the passive `*_dwell` and
   * `*_viewed` events. Wired in CommunityPostCard's `handlePostClick` BEFORE
   * the modifier-key early return so cmd/ctrl-click (open in new tab) still
   * counts as an intent-to-engage click. Uses XHR transport (the click
   * triggers React-router navigation, not a hard pagehide, so beacon is not
   * required to deliver the event).
   *
   * `dwell_ms_before_click` is computed from the most-recent viewport-enter
   * timestamp recorded in the per-feed dwell registry (the same registry
   * F6's community_feed_post_dwell drains). If the user scrolls a card out
   * of view and back in before clicking, the timer reflects the most-recent
   * entry — which is the right reading for "time the user lingered before
   * deciding to click", not the very first time the card was ever seen.
   *
   * If no registry entry exists at click-time (card was clicked before the
   * 500ms viewport-debounce fired, or click happened outside the registry's
   * lifecycle), we report `dwell_ms_before_click: 0`. The card click event
   * still fires — only the dwell-before metric degrades.
   *
   * Outside the feed (hashtag pages, profile snippets, activity feeds), the
   * FeedCardClickContext is not provided, the consumer hook returns null,
   * and the card fires no event — matching the surface scoping of F5/F6.
   *
   * Properties:
   *  - post_id: string — community post id
   *  - position_in_feed: number — 0-indexed position in the visible feed
   *  - dwell_ms_before_click: number — Date.now() - entry.enteredAtMs, or 0
   *    when no registry entry exists
   *  - community_id: string | undefined — the post's owning community id
   *  - feed_tab: string — same semantics as community_feed_post_viewed.feed_tab
   */
  community_feed_post_card_clicked: { apps: ['town_square'], group: 'lurker_engagement' },
  /**
   * Fires when a post-type filter pill under the community feed tabs is toggled.
   * Properties:
   *  - post_type: string | null — the selected CommunityFeedTypeFilter facet
   *    (CRITIQUE | POLL | VIDEO | PRICING_FEEDBACK | ART_RATING), or null when
   *    the filter is cleared.
   *  - feed_tab: string — the active tab (trending | following | new) at toggle.
   */
  community_feed_type_filter_selected: { apps: ['town_square'], group: 'lurker_engagement' },
  /**
   * Fires once each on /community/<slug>/post/<postSlug> when the user's
   * scroll first crosses the 25 / 50 / 75 / 100% threshold of the tracker
   * wrapper's height. Standard finite-page reading-depth telemetry — meaningful
   * on the post detail surface because the content has a fixed length, unlike
   * the infinite-scroll feed (which uses community_feed_post_dwell instead;
   * scroll_50% on an unbounded list is meaningless by construction).
   *
   * "Content" here is the post body + comments section combined (per issue
   * #2452 "pick one and document"). A user who scrolled through every comment
   * is a stronger reader signal than one who only scrolled the post body — so
   * the wrapper measures body+comments together. Banners and the back-link
   * mounted inside the wrapper also count toward the height; a few extra px
   * of chrome does not materially shift the depth, and isolating them would
   * require a second inner ref for no measurable signal improvement.
   *
   * Mechanics (Vercel React best practices):
   *  - {passive: true} scroll listener per §4.2 so the browser does not
   *    block scrolling while the handler runs.
   *  - rAF-throttled so layout reads happen at most once per frame, in the
   *    next paint window, after any pending writes have settled per §7.1.
   *  - Fired-thresholds Set lives in a ref per §5.15 (transient counters
   *    belong in refs, not useState). Re-scrolling past the same threshold
   *    does not re-fire.
   *  - track/postId/communityId stored in live refs so the listener can be
   *    subscribed exactly once per mount per §8.3.
   *
   * Reset semantics: the fired-set resets when postId changes in the same
   * mount cycle. In practice the post detail route remounts on navigation
   * (different route params → different React tree), so each visit gets a
   * fresh tracker — the in-mount reset is defensive for the unlikely
   * shared-mount case.
   *
   * Wired by CommunityPostScrollDepthTracker (mounted in the post detail
   * page server component, wrapping the article + comments div).
   *
   * Properties (identical across all four events):
   *  - post_id: string — community post id
   *  - community_id: string — the post's owning community id
   */
  community_post_scroll_depth_25: { apps: ['town_square'], group: 'lurker_engagement' },
  community_post_scroll_depth_50: { apps: ['town_square'], group: 'lurker_engagement' },
  community_post_scroll_depth_75: { apps: ['town_square'], group: 'lurker_engagement' },
  community_post_scroll_depth_100: { apps: ['town_square'], group: 'lurker_engagement' },
  /**
   * Fires when a user clicks an image inside a community post on the dedicated
   * post-detail route (`/community/<slug>/post/<postSlug>`) to open it in a
   * full-screen lightbox. Strong "engaged with the art" intent signal —
   * complements `community_post_dwell_active_ms` (reading time) and the
   * scroll-depth family (consumption depth).
   *
   * Scope: dedicated post-detail page only. The feed-card carousel
   * (CommunityPostCard) intentionally navigates on click; the in-feed overlay
   * (PostOverlay) is already an enlarged inline surface tracked via the feed
   * events. If the dashboard later wants lightbox signals from those surfaces,
   * they get distinct properties to disambiguate the source.
   *
   * Triggering: wired in PostImageLightbox via the `onImageClick` prop on
   * FeedPostCarousel. Carousel controls (arrows, dots) are excluded, and NSFW
   * placeholders / URL-less tiles never reach the handler — so every emission
   * corresponds to a real lightbox open.
   *
   * Image index is 0-indexed across the post's social-image array in the order
   * the API returned them (matches FeedPostCarousel's render order). Inline
   * `<img>` tags inside TEXT-post HTML are NOT instrumented by this event —
   * those have no canonical index in `socialImages`, and treating them as
   * `image_index: -1` would muddy the dashboard. A separate event would be
   * needed if/when TEXT-post inline image clicks become important.
   *
   * Transport: regular XHR (not beacon). The click does not trigger a
   * navigation, so the standard transport delivers reliably.
   *
   * Properties:
   *  - post_id: string — community post id
   *  - image_index: number — 0-indexed position of the clicked image in the
   *    post's social-image array (matches the carousel render order)
   */
  community_post_image_lightbox_opened: { apps: ['town_square'], group: 'lurker_engagement' },
  /**
   * Fires when a viewer opens the artwork page's full-screen lightbox
   * (`/<profileSlug>/image/<imageSlug>`), by clicking the artwork itself or the
   * labelled expand control in its corner. Both entry points funnel through one
   * handler, so the count is lightbox opens, not clicks on a region.
   *
   * Scope: the town-square artwork detail page only. The community post-detail
   * lightbox has its own `community_post_image_lightbox_opened` — the two stay
   * separate because their surfaces, and the questions asked of them, differ.
   *
   * Triggering: `ImagePageClient.openLightbox`, after the NSFW guard. A flagged
   * artwork renders `NsfwPlaceholder` instead of the work and offers neither
   * entry point, so a suppressed image can never be counted as opened. With a
   * mockup selected the event still fires — the open is on that mockup, and
   * `image_id` stays the underlying artwork.
   *
   * Transport: regular XHR (not beacon). The open does not navigate, so the
   * standard transport delivers reliably.
   *
   * Properties:
   *  - image_id: string — the artwork's image id
   */
  image_lightbox_opened: { apps: ['town_square'], group: 'lurker_engagement' },
  /**
   * Fires the first time the comments-section root crosses ≥50% viewport
   * visibility for ≥500ms on the dedicated post-detail route
   * (`/community/<slug>/post/<postSlug>`). Distinguishes "skimmed the post
   * body" from "read all the way through to comments" — the canonical
   * full-attention engagement signal for the post-detail surface.
   *
   * Fires AT MOST ONCE per page mount. Re-scrolling out of and back into the
   * comments section does not re-fire (the per-mount `useRef<boolean>` guard
   * short-circuits subsequent onEnter invocations from useViewportTracker).
   * Navigating to a different post unmounts the tracker → fresh ref → fresh
   * fire on the next post.
   *
   * Triggering: wired via CommunityCommentSectionTracker, which wraps the
   * existing `<div className="space-y-4">` that contains
   * `<PostPageCommentArea>` (header + composer + sort + list). The viewport
   * threshold is the F3 default (≥50% / ≥500ms) — same as every other
   * `*_viewed` event in this taxonomy.
   *
   * `comment_count_visible` is the count of comment rows present in the
   * SSR'd initial slice — `post.commentThreads.length` (a flat list of root
   * threads + their capped lazy-loaded reply subtrees, capped at 50 root
   * threads server-side under BEST sort). It is NOT the total
   * `post.commentCount` (which includes every reply at every depth) and it
   * is NOT the post-pagination loaded count (the event fires before any
   * additional pages are loaded by definition — viewport-enter is what
   * triggers the engagement signal in the first place). The dashboard SQL
   * answers "how big was the visible comment slice when the user reached
   * the section" — a useful proxy for "did this user scroll past a wall of
   * comments or a near-empty section".
   *
   * Transport: regular XHR (not beacon). The event fires while the user is
   * still on the page; no navigation race.
   *
   * Properties:
   *  - post_id: string — community post id
   *  - comment_count_visible: number — count of comments rendered in the
   *    SSR'd initial slice at first viewport-enter (root threads + their
   *    capped reply subtrees; matches `post.commentThreads.length`)
   */
  community_post_comment_section_viewed: { apps: ['town_square'], group: 'lurker_engagement' },
  /**
   * Fires when an artwork thumbnail in an artist profile's gallery crosses
   * ≥50% viewport visibility for ≥500ms (`/<slug>` gallery tab). Finer-grained
   * than the `profile_view_dwell_active_ms` engagement signal — answers "of
   * this artist's pieces, which ones were actually seen by a viewer?" — the
   * foundational per-artwork attention metric on the H2b acquisition surface.
   *
   * Single-fire semantics PER unique `image_id` PER gallery mount: a
   * `Set<string>` of already-fired image_ids lives in a ref at the gallery
   * level (VirtualizedGallery), and each tile checks/inserts before firing.
   * Re-scrolling past a tile that was already counted does NOT re-fire. The
   * Set is keyed by image_id (not position) so the same image appearing at
   * multiple positions (rare but possible — the existing key prefix
   * `${image.id}-${index}` suggests the data model allows it) only fires
   * once; subsequent positions are no-ops. The reported `position_in_gallery`
   * is whichever entered the viewport first.
   *
   * Reset semantics: the Set is per-mount. The gallery unmounts on tab
   * switch (gallery → about/activity/etc. is a separate route segment under
   * `/[slug]/`), so a return visit to the gallery tab gets a fresh Set and
   * fresh fires for tiles the user re-enters — which matches the dashboard
   * intent ("did this lurker re-engage with the artist's work on a return
   * visit?" is a distinct signal from "did they see it the first time?").
   *
   * Surface scope: artist profile gallery ONLY. The PostOverlay carousel,
   * CommunityPostCard's inline image carousel, and the post-detail page's
   * image lightbox (F12) all live on different surfaces and use their own
   * events. VirtualizedGallery is rendered exclusively by ProfileTabs for
   * the gallery tab — no other consumer.
   *
   * The component is named "VirtualizedGallery" historically; it is NOT
   * actually virtualized as of 2026-05-29 (all tiles render in a CSS grid
   * via `images.map(...)`). Tiles do not unmount during scroll. If the
   * component later gains real virtualization, useViewportTracker's
   * unmount-cleanup path (which calls forceLeave) will correctly stop
   * observing torn-down tiles; the dedup Set still prevents double-firing
   * on re-mount because image_ids persist for the lifetime of the gallery.
   *
   * `profile_user_id` is the artist's `userId` (asfId BIGINT-as-string,
   * same as `profile_view_dwell_active_ms`'s `profile_user_id`) — NOT the
   * artist's slug. Joining against `profile_view_dwell_active_ms` on
   * `profile_user_id` gives the canonical "of all the artists this lurker
   * dwelled on, which specific pieces did they look at?" funnel.
   *
   * Transport: regular XHR. The event fires while the user is on the page;
   * no navigation race so beacon is not required.
   *
   * Properties:
   *  - image_id: string — the artwork's id (matches `image.id` in the
   *    gallery's images array)
   *  - position_in_gallery: number — 0-indexed position at first
   *    viewport-enter; the position recorded is the FIRST one observed for
   *    that image_id even if it appears multiple times
   *  - profile_user_id: string — the artist's userId (asfId)
   */
  profile_artwork_thumb_viewed: { apps: ['town_square'], group: 'lurker_engagement' },
  /**
   * Fires when a viewer clicks an artwork tile in an artist profile's gallery
   * (`/<slug>` gallery tab), navigating to the image detail page at
   * `/<slug>/image/<image-slug>`. The active-engagement counterpart to F15's
   * passive `profile_artwork_thumb_viewed` — answers "of the pieces a viewer
   * saw, which ones did they actively choose to investigate?" — the stronger
   * per-artwork intent signal on the H2b acquisition surface.
   *
   * Wired in VirtualizedGallery's `GalleryItem` via a `useCallback`-stable
   * `handleArtworkClick` that funnels both click targets (the image Link and
   * the title Link) through a single handler. Cmd/ctrl/shift-click for
   * open-in-new-tab also counts as an intent click — the Next.js i18n `<Link>`
   * preserves the browser-default modifier behavior AND fires `onClick` for
   * all click types, so the dashboard sees the click regardless of how the
   * user opened the destination. Same fire-on-every-modifier semantics as
   * F8's `community_feed_post_card_clicked`.
   *
   * The `id="sn_profile_gallery_image_clicked"` marker on the image Link no
   * longer fires anything — element autocapture is off. The click is carried
   * by the structured events: the `sn_profile_gallery_image_clicked` track in
   * ProfileTabs, and F16's `profile_artwork_clicked` with `image_id` +
   * `position_in_gallery` + `profile_user_id` for the dashboard SQL joins.
   *
   * Defensive guard: when `profileUserId` is falsy (missing from the
   * gallery's prop chain), the event is skipped but `onImageClick` STILL
   * runs — navigation is never blocked by missing analytics props. The
   * current call site (ProfileTabs → VirtualizedGallery) always passes a
   * value, so this guard is belt-and-suspenders for future consumers.
   *
   * Surface scope: artist profile gallery ONLY. CommunityPostCard's inline
   * image carousel uses F8 (`community_feed_post_card_clicked`); the
   * post-detail image lightbox uses F12 (`community_post_image_lightbox_opened`).
   * F16 fires exclusively from VirtualizedGallery's tile clicks.
   *
   * `profile_user_id` is the artist's `userId` (asfId BIGINT-as-string,
   * same shape as F14's `profile_view_dwell_active_ms.profile_user_id` and
   * F15's `profile_artwork_thumb_viewed.profile_user_id`). Joining
   * `profile_artwork_clicked` against `profile_artwork_thumb_viewed` on
   * `(profile_user_id, image_id)` gives the "click-through rate per tile"
   * funnel; joining against `profile_view_dwell_active_ms` on
   * `profile_user_id` gives the "of all profiles dwelled on, which had the
   * highest click-through?" rollup.
   *
   * Transport: regular XHR. The click triggers React-router (Next.js App
   * Router) navigation, not a hard `pagehide` — same reasoning as F8 — so
   * beacon is not required to deliver the event.
   *
   * Properties:
   *  - image_id: string — the artwork's id (matches `image.id` in the
   *    gallery's images array, same as F15)
   *  - position_in_gallery: number — 0-indexed position of the clicked tile
   *  - profile_user_id: string — the artist's userId (asfId)
   */
  profile_artwork_clicked: { apps: ['town_square'], group: 'lurker_engagement' },
  /**
   * Fires when a user clicks a platform-authored feed card image link (feed tile
   * or comment overlay) to navigate to `destinationUrl`. Measures outbound
   * engagement on ArtHelper-promoted cards interleaved in community feeds.
   *
   * Wired in `PlatformFeedCard` (feed tile) and `PlatformCardOverlay` (overlay
   * image link). Comment pill clicks do NOT fire this event — they use
   * `platform_feed_card_comment_open`.
   *
   * Transport: regular XHR. External navigation may follow but the click
   * handler runs synchronously before the browser leaves.
   *
   * Properties:
   *  - card_id: string — platform feed card id
   *  - destination_url: string — outbound URL the user navigated toward
   */
  platform_feed_card_click: { apps: ['town_square'], group: 'lurker_engagement' },
  /**
   * Fires when a user opens the comment discussion overlay for a platform feed
   * card (`commentsEnabled` cards only). Distinct from `platform_feed_card_click`
   * — comment pill is an in-app overlay affordance, not outbound navigation.
   *
   * Wired in `PlatformCardOverlayProvider.openPlatformCardOverlay` so every
   * entry path (feed comment pill today, future surfaces) shares one emission.
   *
   * Transport: regular XHR.
   *
   * Properties:
   *  - card_id: string — platform feed card id
   *  - comment_count: number — comment count shown on the pill at open time
   */
  platform_feed_card_comment_open: { apps: ['town_square'], group: 'lurker_engagement' },

  // ---- mockups_gallery (1) ----
  sn_mockups_image_clicked: { apps: ['town_square'], group: 'mockups_gallery' },

  // ---- onboarding (6) ----
  /** @description User viewed (rendered) an onboarding step */
  onboarding_step_viewed: { apps: ['client', 'api'], group: 'onboarding' },
  /** @description User submitted an onboarding step */
  onboarding_step_submitted: { apps: ['client', 'api'], group: 'onboarding' },
  /** @description User skipped an onboarding step */
  onboarding_step_skipped: { apps: ['client', 'api'], group: 'onboarding' },
  /** @description User went back to a previous step */
  onboarding_step_back: { apps: ['client', 'api'], group: 'onboarding' },
  /** @description User completed onboarding (all required steps) */
  onboarding_completed: { apps: ['client', 'api'], group: 'onboarding' },
  /** @description User clicked a CTA on the onboarding Done screen (community or profile) */
  onboarding_done_cta_clicked: { apps: ['client'], group: 'onboarding' },
  /**
   * The onboarding series' message and artist-card items (#4215). Each is turned on alone
   * so its effect can be read alone: clicked ÷ delivered, split by `slug`.
   */
  onboarding_series_item_delivered: { apps: ['api'], group: 'onboarding' },
  onboarding_series_item_clicked: { apps: ['client'], group: 'onboarding' },
  onboarding_series_artist_connected: { apps: ['client'], group: 'onboarding' },

  // ---- bounties (4) ----
  /** @description Member collapsed the bounty band to its tab; `count` is the bounties it holds */
  bounty_band_minimized: { apps: ['town_square', 'client'], group: 'bounties' },
  /** @description Member pulled the minimized bounty band back down */
  bounty_band_expanded: { apps: ['town_square', 'client'], group: 'bounties' },
  /** @description Member tapped the band's arrow to the next bounty; auto-rotation fires nothing */
  bounty_band_next: { apps: ['town_square', 'client'], group: 'bounties' },
  /** @description The minimized band reopened itself because a bounty she had not seen arrived */
  bounty_band_reopened: { apps: ['town_square', 'client'], group: 'bounties' },

  // ---- points (1) ----
  /** @description Member viewed /points; `source` attributes milestone notification/email links */
  points_page_viewed: { apps: ['town_square'], group: 'points' },

  // ---- profile (9) ----
  sn_profile_tab_changed: { apps: ['town_square'], group: 'profile' },
  sn_profile_gallery_image_clicked: { apps: ['town_square'], group: 'profile' },
  sn_profile_fan_count_self_viewed: { apps: ['town_square'], group: 'profile' },
  // The artwork card's overflow menu. The first four mirror the client image
  // manager's four; `deleted` is new to this surface because Delete is, and it is
  // the only destructive item. Full Review, Price It and All Actions fire the Arty card's
  // own `arty_card_*` events with `entry: 'menu'` rather than a menu event of their own.
  // Every one carries `location`: `profile_gallery_context_menu` or
  // `profile_overview_context_menu`.
  //
  // `deleted` counts deletions FROM THE MENU, not every deletion on the profile: the
  // details dialog owns a Delete of its own and fires nothing, exactly as it did
  // before the menu existed. Read it as menu usage, not as a profile delete total.
  sn_profile_artwork_menu_explore_with_ai_clicked: { apps: ['town_square'], group: 'profile' },
  sn_profile_artwork_menu_post_to_social_clicked: { apps: ['town_square'], group: 'profile' },
  sn_profile_artwork_menu_room_mockups_clicked: { apps: ['town_square'], group: 'profile' },
  sn_profile_artwork_menu_view_clicked: { apps: ['town_square'], group: 'profile' },
  sn_profile_artwork_menu_edit_clicked: { apps: ['town_square'], group: 'profile' },
  sn_profile_artwork_menu_deleted: { apps: ['town_square'], group: 'profile' },
  /** The menu opened, carrying `location`; the denominator for the item clicks above. */
  sn_profile_artwork_menu_opened: { apps: ['town_square'], group: 'profile' },
  /**
   * The artwork-comments switch in the owner settings bar, carrying the new
   * value as `is_enabled`. town-square rather than client: the client profile
   * editor was retired (`ProfileSettings.tsx` is admin-only now) and this switch
   * ships only in `ProfileSettingsBar`.
   *
   * A CHANGE signal, never a level one — see the note on
   * `artist_artwork_comment_received` for why the standing share of artists with
   * comments on is a count over `profiles.isArtworkCommentsEnabled` instead.
   */
  artwork_comments_switch_toggled: { apps: ['town_square'], group: 'profile' },
  /**
   * A one-tap visibility flip on a profile artwork, carrying the new `is_public`
   * and a `location` — `gallery_card` (Remove from profile) or `private_section`
   * (Add to profile). A change signal, one event per flip.
   */
  sn_profile_artwork_visibility_toggled: { apps: ['town_square'], group: 'profile' },
  /**
   * The "New uploads are public by default" switch in the owner settings bar,
   * carrying the new value as `is_enabled`. Writes `User.isDefaultArtworkPublic`.
   */
  default_artwork_public_toggled: { apps: ['town_square'], group: 'profile' },

  // ---- profile_claiming (6) ----
  claim_page_viewed: { apps: ['client'], group: 'profile_claiming' },
  claim_support_clicked: { apps: ['client'], group: 'profile_claiming' },
  claim_email_submitted: { apps: ['client'], group: 'profile_claiming' },
  claim_verification_code_entered: { apps: ['client'], group: 'profile_claiming' },
  claim_account_created: { apps: ['client'], group: 'profile_claiming' },
  claim_blocked_existing_account: { apps: ['client'], group: 'profile_claiming' },

  // ---- profile_message (1) ----
  sn_profile_message_clicked: { apps: ['town_square'], group: 'profile_message' },

  // ---- referral_tracking (17) ----
  sn_referral_link_received: { apps: ['town_square'], group: 'referral_tracking' },
  sn_referral_link_copied: { apps: ['town_square', 'client'], group: 'referral_tracking' },
  /** One event per share handoff, whatever the door: method = native|sms|whatsapp|email|copy|qr. */
  sn_referral_share: { apps: ['town_square', 'client'], group: 'referral_tracking' },
  /** A directed gift-subscription invite was sent to a specific email. */
  sn_gift_invite_sent: { apps: ['town_square', 'client'], group: 'referral_tracking' },
  /** A pending gift invite's email was resent from the referrals dashboard. */
  sn_gift_invite_resent: { apps: ['town_square'], group: 'referral_tracking' },
  /** A pending gift invite was cancelled (slot freed to reassign). */
  sn_gift_invite_canceled: { apps: ['town_square'], group: 'referral_tracking' },
  /**
   * The gift-invite lander resolved its token. `state` is valid|expired|canceled|used|invalid|incomplete|unchecked|referral
   * (incomplete = bare `/invite` with nothing usable stashed; unchecked = the status check failed, or was skipped
   * for a signed-in visitor whose link names the gifter, who claims instead; referral = a hash-only link after a dead
   * stashed token was dropped); `recovered` = params came from the stash, not the URL; `staleState` = the dead
   * stashed token's state when one was dropped (then `recovered` is false), else null;
   * `referrer` = the referring page's origin only (never its path or query, which can carry a token).
   */
  sn_gift_invite_viewed: { apps: ['town_square'], group: 'referral_tracking' },
  /**
   * A presented gift token recorded a referral. `via` signup|claim; `outcome` gift (friend reward
   * applied) | referral_only (expired/canceled offer — referrer link only); `emailMatched` = the
   * claimant's email is the invited one.
   */
  sn_gift_invite_claimed: { apps: ['api'], group: 'referral_tracking' },
  /**
   * A presented gift token recorded nothing. `via` signup|claim; `reason` used|invalid|ineligible|expired|canceled|own_invite —
   * expired/canceled = an ineligible viewer (already referred, older than the invite) on a lapsed invite; own_invite = the
   * gifter presented their own token.
   */
  sn_gift_invite_claim_failed: { apps: ['api'], group: 'referral_tracking' },
  /** The welcome wizard confirmed a received gift subscription to its recipient. */
  sn_welcome_gift_shown: { apps: ['client'], group: 'referral_tracking' },
  /**
   * A new signup was pinned to an acquisition strategy (#4605). `strategyVersion`, `strategyName`,
   * `gateMode`, `gated` (held on the waitlist), `referredIn` (arrived through a referral link).
   */
  acquisition_assigned: { apps: ['api'], group: 'referral_tracking' },
  /** A waitlisted signup got in. `via` referral_unlock|invite|admin_release, `strategyVersion`. */
  waitlist_unlocked: { apps: ['api'], group: 'referral_tracking' },
  /** The waitlist page rendered for a held signup. `strategyVersion`, `requiredReferrals`, `qualifiedCount`. */
  sn_waitlist_viewed: { apps: ['town_square'], group: 'referral_tracking' },
  /** A referred-in signup saw the "you're in, {inviter} let you in" moment. `strategyVersion`. */
  sn_invite_welcome_viewed: { apps: ['town_square'], group: 'referral_tracking' },
  /** A cold visitor saw the invite-only signup gate instead of the account form. `strategyVersion`, `requiredReferrals`. */
  sn_signup_gate_viewed: { apps: ['town_square'], group: 'referral_tracking' },
  /** The cold visitor chose "Join the waitlist" (the account form appears next). `strategyVersion`. */
  sn_signup_gate_join_clicked: { apps: ['town_square'], group: 'referral_tracking' },
  /** A held member opened the waiting wizard from the waitlist page. `status` not_started|in_progress. */
  sn_waitlist_build_profile_clicked: { apps: ['town_square'], group: 'referral_tracking' },
  /** The cold visitor pasted a friend's invite on the gate and was sent to the invite lander. `strategyVersion`. */
  sn_signup_gate_invite_entered: { apps: ['town_square'], group: 'referral_tracking' },

  // ---- search (1) ----
  sn_search_performed: { apps: ['town_square'], group: 'search' },

  // ---- share_button (10) ----
  sn_share_button_clicked: { apps: ['town_square'], group: 'share_button' },
  sn_share_modal_copy_button_clicked: { apps: ['town_square'], group: 'share_button' },
  sn_share_modal_facebook_button_clicked: { apps: ['town_square'], group: 'share_button' },
  sn_share_modal_pinterest_button_clicked: { apps: ['town_square'], group: 'share_button' },
  sn_share_modal_reddit_button_clicked: { apps: ['town_square'], group: 'share_button' },
  sn_share_modal_twitter_button_clicked: { apps: ['town_square'], group: 'share_button' },
  sn_share_modal_whatsapp_button_clicked: { apps: ['town_square'], group: 'share_button' },
  sn_share_modal_linkedin_button_clicked: { apps: ['town_square'], group: 'share_button' },
  sn_share_modal_bluesky_button_clicked: { apps: ['town_square'], group: 'share_button' },
  sn_share_modal_threads_button_clicked: { apps: ['town_square'], group: 'share_button' },

  // ---- share_kit (11) ----
  /**
   * The artist share kit (`/share-kit`): ready-made post/story graphics of the artist's own
   * work. Every share carries `card` (the headline key), `shape` (post|story) and, for link
   * shares, `method` — so we can see which design gets shared and where. `surface` on the
   * entry click says which doorway brought the artist in.
   */
  sn_share_kit_entry_clicked: { apps: ['town_square'], group: 'share_kit' },
  sn_share_kit_viewed: { apps: ['town_square'], group: 'share_kit' },
  sn_share_kit_artwork_selected: { apps: ['town_square'], group: 'share_kit' },
  sn_share_kit_shape_switched: { apps: ['town_square'], group: 'share_kit' },
  sn_share_kit_link_shared: { apps: ['town_square'], group: 'share_kit' },
  sn_share_kit_image_shared: { apps: ['town_square'], group: 'share_kit' },
  sn_share_kit_image_downloaded: { apps: ['town_square'], group: 'share_kit' },
  sn_share_kit_caption_copied: { apps: ['town_square'], group: 'share_kit' },
  sn_share_kit_upgrade_clicked: { apps: ['town_square'], group: 'share_kit' },
  /** A stranger opened an artwork or profile from a share-kit card link. */
  sn_share_kit_link_landed: { apps: ['town_square'], group: 'share_kit' },
  /** That stranger tapped the supporter bar's Comment / Follow (sign-up when signed out). */
  sn_share_kit_supporter_cta_clicked: { apps: ['town_square'], group: 'share_kit' },

  // ---- sign_up_button (1) ----
  sn_sign_up_button_clicked: { apps: ['town_square'], group: 'sign_up_button' },

  // ---- social_network (79) ----
  // The 15 `dm_*` entries below carry both apps: town-square's MessagingDock port
  // (issue #2802) mirrors the client `dm_*` taxonomy name-for-name.
  /**
   * @description Fires when a viewer follows or unfollows from a Suggested connections
   * card on /network. `following` carries the state AFTER the tap, so a toggle reads as
   * one event with a direction rather than two names.
   */
  network_suggested_follow_toggled: { apps: ['client'], group: 'social_network' },
  network_hub_new_message_clicked: { apps: ['client'], group: 'social_network' },
  network_hub_invitation_accepted: { apps: ['client'], group: 'social_network' },
  network_hub_invitation_accept_failed: { apps: ['client'], group: 'social_network' },
  network_hub_invitation_ignored: { apps: ['client'], group: 'social_network' },
  network_hub_invitation_ignore_failed: { apps: ['client'], group: 'social_network' },
  network_hub_nav_card_clicked: { apps: ['client'], group: 'social_network' },
  network_hub_show_all_clicked: { apps: ['client'], group: 'social_network' },
  network_requests_tab_changed: { apps: ['client'], group: 'social_network' },
  network_request_accepted: { apps: ['client'], group: 'social_network' },
  network_request_accept_failed: { apps: ['client'], group: 'social_network' },
  connection_say_hi_nudge_shown: { apps: ['client'], group: 'social_network' },
  connection_say_hi_nudge_dismissed: { apps: ['client'], group: 'social_network' },
  connection_say_hi_nudge_compose_opened: { apps: ['client'], group: 'social_network' },
  /**
   * The pending-requests banner at the top of both apps (spec #4092). `shown` fires once per
   * newest pending connection id, so a refetch that changes nothing does not re-count it.
   * Properties: connection_id, pending_count (+ requester_user_id on accept_clicked).
   */
  pending_requests_banner_shown: { apps: ['client', 'town_square'], group: 'social_network' },
  pending_requests_banner_accept_clicked: { apps: ['client', 'town_square'], group: 'social_network' },
  pending_requests_banner_hidden: { apps: ['client', 'town_square'], group: 'social_network' },
  network_request_ignored: { apps: ['client'], group: 'social_network' },
  network_request_ignore_failed: { apps: ['client'], group: 'social_network' },
  network_request_withdrawn: { apps: ['client'], group: 'social_network' },
  network_request_withdraw_failed: { apps: ['client'], group: 'social_network' },
  network_connections_new_message_clicked: { apps: ['client'], group: 'social_network' },
  network_connections_search_performed: { apps: ['client'], group: 'social_network' },
  network_hub_find_people_submitted: { apps: ['client'], group: 'social_network' },
  network_member_search_profile_opened: { apps: ['client'], group: 'social_network' },
  network_connection_message_clicked: { apps: ['client'], group: 'social_network' },
  network_connection_remove_confirmed: { apps: ['client'], group: 'social_network' },
  network_connection_remove_failed: { apps: ['client'], group: 'social_network' },
  network_connection_block_confirmed: { apps: ['client'], group: 'social_network' },
  network_connection_block_failed: { apps: ['client'], group: 'social_network' },
  network_connection_remove_dialog_cancelled: { apps: ['client'], group: 'social_network' },
  network_connection_block_dialog_cancelled: { apps: ['client'], group: 'social_network' },
  network_connection_reported: { apps: ['client'], group: 'social_network' },
  network_hub_invitation_reported: { apps: ['client'], group: 'social_network' },
  network_hub_connect_clicked: { apps: ['client'], group: 'social_network' },
  network_hub_connect_send_success: { apps: ['client'], group: 'social_network' },
  network_hub_connect_send_error: { apps: ['client'], group: 'social_network' },
  network_hub_connect_intro_dismissed: { apps: ['client'], group: 'social_network' },
  network_hub_connect_message_added_clicked: { apps: ['client'], group: 'social_network' },
  network_hub_suggestion_dismissed: { apps: ['client'], group: 'social_network' },
  network_hub_suggestion_dismiss_failed: { apps: ['client'], group: 'social_network' },
  network_hub_suggestion_dismiss_undone: { apps: ['client'], group: 'social_network' },
  network_hub_suggestion_section_viewed: { apps: ['client'], group: 'social_network' },
  network_hub_suggestion_card_clicked: { apps: ['client'], group: 'social_network' },
  network_hub_suggestion_artwork_opened: { apps: ['client'], group: 'social_network' },
  network_hub_mutual_connections_clicked: { apps: ['client'], group: 'social_network' },
  network_hub_suggestions_show_all_clicked: { apps: ['client'], group: 'social_network' },
  network_engaged_with_you_clicked: { apps: ['client'], group: 'social_network' },
  network_catch_up_tab_changed: { apps: ['client'], group: 'social_network' },
  network_catch_up_item_clicked: { apps: ['client'], group: 'social_network' },
  recommended_connections_digest_landed: { apps: ['client'], group: 'social_network' },
  recommended_connections_digest_connect_clicked: { apps: ['client'], group: 'social_network' },
  recommended_connections_digest_connect_success: { apps: ['client'], group: 'social_network' },
  network_request_reported: { apps: ['client'], group: 'social_network' },
  notification_connect_clicked: { apps: ['client'], group: 'social_network' },
  notification_connect_send_success: { apps: ['client'], group: 'social_network' },
  notification_connect_send_error: { apps: ['client'], group: 'social_network' },
  notification_connect_sent_prompt_shown: { apps: ['client'], group: 'social_network' },
  notification_connect_sent_prompt_dismissed: { apps: ['client'], group: 'social_network' },
  notification_connect_message_added_clicked: { apps: ['client'], group: 'social_network' },
  notification_connect_message_added_success: { apps: ['client'], group: 'social_network' },
  notification_connect_message_added_error: { apps: ['client'], group: 'social_network' },
  notification_quick_reply_sent: { apps: ['client'], group: 'social_network' },
  notification_love_clicked: { apps: ['client'], group: 'social_network' },
  notification_toast_shown: { apps: ['client', 'town_square'], group: 'social_network' },
  notification_toast_clicked: { apps: ['client', 'town_square'], group: 'social_network' },
  notification_socket_lost: { apps: ['client', 'town_square'], group: 'social_network' },
  notification_socket_recovered: { apps: ['client', 'town_square'], group: 'social_network' },
  dm_inbox_thread_clicked: { apps: ['client'], group: 'social_network' },
  dm_inbox_search_performed: { apps: ['client'], group: 'social_network' },
  dm_inbox_new_message_clicked: { apps: ['client'], group: 'social_network' },
  dm_thread_message_sent: { apps: ['client', 'town_square'], group: 'social_network' },
  dm_thread_message_failed: { apps: ['client', 'town_square'], group: 'social_network' },
  /** Payload: { thread_id: string; profile_user_id: string; source: 'thread_header' } (issue #3194). */
  dm_thread_header_profile_link_clicked: { apps: ['client', 'town_square'], group: 'social_network' },
  /** Payload: { thread_id: string; guild_id: string; source: 'thread_header' }. A guild room's header links back to the guild. */
  dm_thread_header_guild_link_clicked: { apps: ['client', 'town_square'], group: 'social_network' },
  dm_new_message_send_clicked: { apps: ['client', 'town_square'], group: 'social_network' },
  dm_new_message_send_success: { apps: ['client', 'town_square'], group: 'social_network' },
  dm_new_message_send_failed: { apps: ['client', 'town_square'], group: 'social_network' },
  dm_new_message_cancelled: { apps: ['client'], group: 'social_network' },
  dm_recipient_added: { apps: ['client', 'town_square'], group: 'social_network' },
  dm_recipient_removed: { apps: ['client', 'town_square'], group: 'social_network' },
  dm_dock_main_expanded: { apps: ['client', 'town_square'], group: 'social_network' },
  dm_dock_main_minimized: { apps: ['client', 'town_square'], group: 'social_network' },
  dm_dock_compose_opened: { apps: ['client', 'town_square'], group: 'social_network' },
  dm_dock_thread_opened: { apps: ['client', 'town_square'], group: 'social_network' },
  dm_dock_thread_minimized: { apps: ['client', 'town_square'], group: 'social_network' },
  dm_dock_thread_closed: { apps: ['client', 'town_square'], group: 'social_network' },
  dm_spam_warning_shown: { apps: ['client', 'town_square'], group: 'social_network' },
  dm_user_blocked: { apps: ['client', 'town_square'], group: 'social_network' },
  verify_external_link_clicked: { apps: ['client'], group: 'social_network' },
  hma_billing_portal_opened: { apps: ['client'], group: 'social_network' },

  // ---- social_network_actions (18) ----
  sn_post_created: { apps: ['town_square'], group: 'social_network_actions' },
  sn_community_post_created: { apps: ['town_square'], group: 'social_network_actions' },
  sn_comment_created: { apps: ['town_square'], group: 'social_network_actions' },
  sn_community_created: { apps: ['town_square'], group: 'social_network_actions' },
  sn_post_liked: { apps: ['town_square'], group: 'social_network_actions' },
  sn_comment_liked: { apps: ['town_square'], group: 'social_network_actions' },
  sn_community_post_upvoted: { apps: ['town_square'], group: 'social_network_actions' },
  sn_community_post_downvoted: { apps: ['town_square'], group: 'social_network_actions' },
  sn_community_post_bookmarked: { apps: ['town_square'], group: 'social_network_actions' },
  sn_community_post_unbookmarked: { apps: ['town_square'], group: 'social_network_actions' },
  sn_community_comment_upvoted: { apps: ['town_square'], group: 'social_network_actions' },
  sn_community_comment_downvoted: { apps: ['town_square'], group: 'social_network_actions' },
  sn_community_poll_voted: { apps: ['town_square'], group: 'social_network_actions' },
  sn_community_poll_unvoted: { apps: ['town_square'], group: 'social_network_actions' },
  sn_community_price_estimate_submitted: { apps: ['town_square'], group: 'social_network_actions' },
  sn_community_price_estimate_updated: { apps: ['town_square'], group: 'social_network_actions' },
  sn_community_pricing_post_created: { apps: ['town_square'], group: 'social_network_actions' },
  sn_community_art_rating_submitted: { apps: ['town_square'], group: 'social_network_actions' },
  sn_community_art_rating_updated: { apps: ['town_square'], group: 'social_network_actions' },
  sn_community_art_rating_comment_created: { apps: ['town_square'], group: 'social_network_actions' },
  sn_community_rating_post_created: { apps: ['town_square'], group: 'social_network_actions' },
  sn_community_poll_post_created: { apps: ['town_square'], group: 'social_network_actions' },
  sn_community_which_is_better_post_created: { apps: ['town_square'], group: 'social_network_actions' },
  /** A one-click `/ask/...` link (email or anywhere) reached a signed-in artist. Props: ask_type, mode. */
  sn_community_ask_link_opened: { apps: ['town_square'], group: 'social_network_actions' },
  /** The `/post` form of an ask link published from its confirm screen. Props: ask_type, post_id. */
  sn_community_ask_link_posted: { apps: ['town_square'], group: 'social_network_actions' },
  /** An ask link could not draft or post. Props: ask_type, mode, stage. */
  sn_community_ask_link_failed: { apps: ['town_square'], group: 'social_network_actions' },
  sn_post_shared: { apps: ['town_square'], group: 'social_network_actions' },
  sn_community_joined: { apps: ['town_square'], group: 'social_network_actions' },
  sn_user_followed: { apps: ['client', 'town_square'], group: 'social_network_actions' },
  sn_user_unfollowed: { apps: ['client', 'town_square'], group: 'social_network_actions' },
  /** Payload: `{ target_user_id, surface }` — once per open of the desktop profile hover card. */
  sn_profile_hover_card_opened: { apps: ['client', 'town_square'], group: 'social_network_actions' },

  // ---- user_management (9) ----
  signed_in: { apps: ['client'], group: 'user_management' },
  signed_out: { apps: ['client'], group: 'user_management' },
  /**
   * Payload: `{ entry_point }` — the control that opened the uploader. The
   * client dashboard sends `'client_dashboard'`; the town-square profile page
   * sends `'profile_overview_teaser' | 'profile_gallery_tab' | 'profile_gallery_empty'`.
   * Typed union lives in `client/src/context/analytics/amplitude/events.ts`.
   */
  add_artwork_clicked: { apps: ['client', 'town_square'], group: 'user_management' },
  new_post_clicked: { apps: ['client'], group: 'user_management' },
  invite_artists_clicked: { apps: ['client'], group: 'user_management' },
  profile_updated: { apps: ['client', 'town_square'], group: 'user_management' },
  daily_sms_capture_agent: { apps: ['client'], group: 'user_management' },
  tab_tapped: { apps: ['client'], group: 'user_management' },
  payment_success: { apps: ['client', 'api'], group: 'user_management' },

  // ---- verify_hma (9) ----
  sn_verify_page_viewed: { apps: ['town_square'], group: 'verify_hma' },
  sn_verify_cta_clicked: { apps: ['town_square'], group: 'verify_hma' },
  sn_verify_pricing_modal_opened: { apps: ['town_square'], group: 'verify_hma' },
  sn_verify_pricing_modal_closed: { apps: ['town_square'], group: 'verify_hma' },
  sn_verify_plan_interval_selected: { apps: ['town_square'], group: 'verify_hma' },
  sn_verify_checkout_clicked: { apps: ['town_square'], group: 'verify_hma' },
  sn_verify_checkout_redirect_to_signin: { apps: ['town_square'], group: 'verify_hma' },
  sn_verify_checkout_purchase_returned: { apps: ['town_square'], group: 'verify_hma' },
  sn_verify_header_link_clicked: { apps: ['town_square'], group: 'verify_hma' },

  // ---- artist_standard (9) ----
  /**
   * The Artist Standard — one normative score for the artist, with the profile demoted
   * to one pillar of four.
   *
   * Instrumented from the first prototype render rather than after the fact. The profile
   * completeness meter shipped with NOTHING, so its effect had to be reconstructed from
   * page traffic and the model-profile link's contribution could not be measured at all.
   * Wiring the events alongside the prototype means going live is a data-source change,
   * not an instrumentation project.
   *
   * NOTE: while `artist_standard_prototype` is the only surface firing these, they
   * describe FIXTURE personas, not real artists — which is why every one of them carries
   * `is_prototype`. Any read must filter on it or it is measuring a demo.
   */

  /**
   * The composite card mounted. Fires once per mount, guarded by a ref so a score
   * recomputed on an in-place edit cannot re-fire it per keystroke.
   *
   * Properties: `score` (0-100), `rung`, `next_step` (the single most valuable open step
   * key, or `null` at the top), `capped_by_body_of_work` (the gate is holding the rung
   * down below what the score alone would earn), `ladder`, `is_prototype`.
   */
  artist_standard_viewed: { apps: ['town_square'], group: 'artist_standard' },
  /**
   * A pillar row opened. The only signal separating "the artist read the evidence" from
   * "the artist saw a number", which is the mechanism under test.
   *
   * Properties: `pillar`, `earned`, `weight`, `score`, `is_prototype`.
   */
  artist_standard_pillar_expanded: { apps: ['town_square'], group: 'artist_standard' },
  /**
   * A step link clicked through to the surface that closes it.
   *
   * Properties: `step`, `pillar`, `href`, `score`, `is_next_step` — whether this was THE
   * one action offered, which is what tells us whether one-action-at-a-time is working —
   * and `is_prototype`.
   */
  artist_standard_step_clicked: { apps: ['town_square'], group: 'artist_standard' },
  /**
   * A step was OPENED IN PLACE — the modal, not a navigation. Distinct from
   * `..._step_clicked`, which is now only the press that actually leaves for the
   * surface that closes the step. Keeping them apart is the whole measurement: it
   * separates "the sell got her to look" from "the sell got her to go", and a single
   * event would make a step with a compelling modal and a dead-end destination
   * indistinguishable from one that works.
   *
   * Properties: `step`, `pillar`, `score`, `is_next_step`, `surface`
   * (`bar` | `bar_full_list`), `is_prototype`.
   */
  artist_standard_step_opened: { apps: ['town_square'], group: 'artist_standard' },
  /**
   * The invite panel opened from a quest. The copy press itself rides the existing
   * `sn_referral_link_copied` with `source: 'quest-band'`; this event is the top of that
   * funnel. Properties: `quest`, `is_prototype`.
   */
  quest_invite_opened: { apps: ['town_square'], group: 'artist_standard' },
  /** The People tab rendered with participants. Properties: `quest`, `participant_count`. */
  quest_people_viewed: { apps: ['town_square'], group: 'artist_standard' },
  /**
   * A step went from incomplete to complete. Fired on the TRANSITION, by diffing against
   * the previous render — a page that loads with the step already done must not report a
   * completion on every visit.
   *
   * Properties: `step`, `pillar`, `score`, `previous_score`, `is_prototype`.
   */
  artist_standard_step_completed: { apps: ['town_square'], group: 'artist_standard' },
  /**
   * The artist moved up a rung. Also a transition event, for the same reason.
   *
   * Properties: `rung`, `previous_rung`, `ladder`, `score`, `is_prototype`.
   */
  artist_standard_rung_reached: { apps: ['town_square'], group: 'artist_standard' },
  /**
   * The top rung — the artist meets the standard and the card disappears for good.
   * Separate from `..._rung_reached` because it is the success metric of the whole
   * feature, and because it is the last event this surface ever fires for that artist.
   *
   * Properties: `score`, `ladder`, `is_prototype`.
   */
  artist_standard_top_rung_reached: { apps: ['town_square'], group: 'artist_standard' },
  // ---- profile_completeness (4) ----
  /**
   * The completeness meter and the notification email that carries it, which
   * until now fired NOTHING. The v4.5.27 read had to infer model-profile clicks
   * from `/havalina` page traffic — a proxy that cannot see a modal and cannot
   * separate an email click from an on-page one. These four make the next read
   * direct.
   *
   * `profile_updated` (user_management, above) is the conversion event they are
   * meant to be joined against.
   */

  /**
   * The owner's meter mounted on their own profile. Fires once per mount, so a
   * tab change within the profile re-fires — the meter is remounted by the route
   * segment, and a view is a view.
   *
   * Properties: `score` (0-100 weighted, NOT `profiles.profileCompleteness`),
   * `next_field` (the single most valuable missing field key, or `null` at 100%).
   */
  profile_completeness_meter_viewed: { apps: ['town_square'], group: 'profile_completeness' },
  /**
   * The "see a finished profile" link beside the meter. Properties: `score`,
   * `example_url` — the medium-matched model profile, which is why the URL is a
   * property rather than something to infer from the destination's traffic.
   */
  profile_completeness_example_clicked: { apps: ['town_square'], group: 'profile_completeness' },
  /**
   * A landing from any notification-email link, read off the UTM parameters the
   * email already carries. `link` is the `utm_content` value
   * (`profile` | `model_profile` | `public_profile` | `notifications` | `settings`),
   * which is what separates the email's contribution from the on-page meter.
   * Settings lands on the client; the other destinations land on town-square.
   */
  notification_email_link_clicked: { apps: ['town_square', 'client'], group: 'profile_completeness' },
} as const;

/** Products that fire Amplitude events. */
export type AmplitudeApp = 'client' | 'town_square' | 'api';

/** Every Amplitude event name that exists, anywhere. */
export type AmplitudeEventName = keyof typeof AMPLITUDE_EVENTS;

/** Every group an event can belong to. */
export type AmplitudeEventGroup = (typeof AMPLITUDE_EVENTS)[AmplitudeEventName]['group'];

type AppOf<K extends AmplitudeEventName> = (typeof AMPLITUDE_EVENTS)[K]['apps'][number];

/** Names the client app fires — filtered on app membership, never hand-listed. */
export type ClientEventName = {
  [K in AmplitudeEventName]: 'client' extends AppOf<K> ? K : never;
}[AmplitudeEventName];

/** Names the town-square app fires. */
export type TownSquareEventName = {
  [K in AmplitudeEventName]: 'town_square' extends AppOf<K> ? K : never;
}[AmplitudeEventName];

/** Names the api fires server-side. */
export type ApiEventName = {
  [K in AmplitudeEventName]: 'api' extends AppOf<K> ? K : never;
}[AmplitudeEventName];

export type AmplitudeEventGroupDisplay = {
  group: AmplitudeEventGroup;
  events: { name: AmplitudeEventName; apps: readonly AmplitudeApp[] }[];
};

/**
 * The catalog grouped for display — every event listed once under its group
 * alongside the apps that fire it. Groups and events are both name-sorted so
 * a rendered select is stable.
 */
export function groupedEvents(): AmplitudeEventGroupDisplay[] {
  const names = Object.keys(AMPLITUDE_EVENTS) as AmplitudeEventName[];

  const byGroup = names.sort().reduce((memo, name) => {
    const { group, apps } = AMPLITUDE_EVENTS[name];
    const events = memo.get(group) ?? [];
    events.push({ name, apps });
    memo.set(group, events);
    return memo;
  }, new Map<AmplitudeEventGroup, AmplitudeEventGroupDisplay['events']>());

  return [...byGroup.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([group, events]) => ({ group, events }));
}
